import { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './useAuth';

interface UpcomingInvoice {
  id: string;
  amount_due: number;
  amount_remaining: number;
  subtotal: number;
  total: number;
  currency: string;
  period_start: number;
  period_end: number;
  discount: {
    coupon: {
      id: string;
      name: string | null;
      percent_off: number | null;
      amount_off: number | null;
      duration: string;
      duration_in_months: number | null;
    };
    start: number;
    end: number | null;
  } | null;
  total_discount_amounts: Array<{
    amount: number;
    discount: string;
  }> | null;
  lines: Array<{
    description: string | null;
    amount: number;
    quantity: number | null;
    period: {
      start: number;
      end: number;
    };
  }>;
}

interface UseUpcomingInvoiceResult {
  upcomingInvoice: UpcomingInvoice | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Hook to fetch the upcoming invoice for the current user
 * This shows what they'll be charged on their next billing cycle,
 * including any coupon discounts
 */
export function useUpcomingInvoice(): UseUpcomingInvoiceResult {
  const { user } = useAuth();
  const [upcomingInvoice, setUpcomingInvoice] = useState<UpcomingInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUpcomingInvoice = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase.functions.invoke('get-upcoming-invoice', {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('Upcoming invoice response:', { data, error: fetchError });

      if (fetchError) {
        console.log('Edge function error details:', fetchError);
        // Handle "No billing account found" as a valid empty state
        if (fetchError.message?.includes('404') || fetchError.message?.includes('No billing account')) {
          setUpcomingInvoice(null);
          return;
        }
        throw fetchError;
      }

      if (data) {
        setUpcomingInvoice(data.upcoming_invoice);
      }
    } catch (err) {
      console.error('Error fetching upcoming invoice:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch upcoming invoice');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    await fetchUpcomingInvoice();
  };

  useEffect(() => {
    if (user) {
      fetchUpcomingInvoice();
    } else {
      setUpcomingInvoice(null);
      setLoading(false);
    }
  }, [user]);

  return {
    upcomingInvoice,
    loading,
    error,
    refresh
  };
}

/**
 * Format currency with proper symbol and decimals
 */
export function formatCurrency(amount: number, currency: string = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100); // Stripe amounts are in cents
}

/**
 * Calculate the discount amount from an upcoming invoice
 */
export function getDiscountAmount(invoice: UpcomingInvoice | null): number {
  if (!invoice || !invoice.total_discount_amounts) {
    return 0;
  }

  return invoice.total_discount_amounts.reduce((sum, discount) => sum + discount.amount, 0);
}

/**
 * Get a human-readable description of the discount
 */
export function getDiscountDescription(invoice: UpcomingInvoice | null): string | null {
  if (!invoice?.discount?.coupon) {
    return null;
  }

  const coupon = invoice.discount.coupon;

  if (coupon.percent_off) {
    return `${coupon.percent_off}% off`;
  }

  if (coupon.amount_off) {
    return `${formatCurrency(coupon.amount_off, invoice.currency)} off`;
  }

  return 'Discount applied';
}
